import { InsideType } from '../../../constants/enum';
import * as THREE from 'three';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';

export default class InsideCabinetHandler {
  // Constants for magic numbers
  static DRAWER_FRONT_BACK_OFFSET = 0.061;
  static DRAWER_BOTTOM_OFFSET = 0.027;
  static THICKNESS_BASE = 0.017;

  // Component name constants
  static COMPONENT_NAMES = {
    MAT_TRUOC: 'MAT_TRUOC',
    MAT_SAU: 'MAT_SAU',
    MAT_PHAI: 'MAT_PHAI',
    MAT_TRAI: 'MAT_TRAI',
    TAM_DAY: 'TAM_DAY',
    RAY_PHAI: 'RAY_PHAI',
    RAY_TRAI: 'RAY_TRAI',
    NOC_U: 'NOC-U',
    VACH_TRAI_U: 'VACH-TRAI-U',
    VACH_PHAI_U: 'VACH-PHAI-U',
    GG_TRAI: 'GG-TRAI',
    GG_PHAI: 'GG-PHAI',
  };

  /**
   * Scales the depth of cabinet components
   * @param {Object} gltfScene - The GLTF scene object
   * @param {number} sizeScale - The target size for scaling
   */
  static scaleDepth(gltfScene, sizeScale, clonedModule) {
    if (!this._validateInputs(gltfScene, sizeScale)) return;

    const { type } = gltfScene.userData;

    switch (type) {
      case InsideType.HPANEL:
      case InsideType.VPANEL:
        this._scaleSimplePanel(gltfScene, sizeScale, 'x');
        break;
      case InsideType.DRAWER:
        this._scaleDrawerDepth(gltfScene, sizeScale, clonedModule);
        break;
      case InsideType.U_DRAWER:
        this._scaleUDrawerDepth(gltfScene, sizeScale);
        break;
      case 5:
        // Reserved for future implementation
        break;
      default:
        console.warn(`Unknown cabinet type: ${type}`);
    }
  }

  /**
   * Scales the width of cabinet components
   * @param {Object} gltfScene - The GLTF scene object
   * @param {number} sizeScale - The target size for scaling
   */
  static scaleWidth(
    gltfScene,
    sizeScale,
    originalSize,
    module,
    scaleCabinetShelfWidth
  ) {
    if (!this._validateInputs(gltfScene, sizeScale)) return;

    const { type } = gltfScene.userData;

    switch (type) {
      case InsideType.HPANEL:
        this._scaleSimplePanel(gltfScene, sizeScale, 'z');
        break;
      case InsideType.VPANEL:
        this._scaleSimplePanel(gltfScene, sizeScale, 'x');
        break;
      case InsideType.DRAWER:
        this._scaleDrawerWidth(gltfScene, sizeScale, originalSize);
        break;
      case InsideType.U_DRAWER:
        this._scaleUDrawerWidth(gltfScene, sizeScale, originalSize);
        if (module) {
          const orientation = GeometryUtils.getModuleOrientation(module.group);
          this.resetScaleUdrawerChild(
            module,
            gltfScene,
            orientation,
            scaleCabinetShelfWidth
          );
        }

        break;
      case 5:
        // Reserved for future implementation
        break;
      default:
        const scaleZ = sizeScale / gltfScene.userData.sizeDefault.z;
        gltfScene.scale.z = scaleZ;
        break;
    }
  }

  /**
   * Scales the width of cabinet components
   * @param {Object} gltfScene - The GLTF scene object
   * @param {number} sizeScale - The target size for scaling
   */
  static scaleHeight(gltfScene, sizeScale) {
    if (!this._validateInputs(gltfScene, sizeScale)) return;

    const { type } = gltfScene.userData;

    switch (type) {
      case InsideType.HPANEL:
        this._scaleSimplePanel(gltfScene, sizeScale, 'z');
        break;
      case InsideType.VPANEL:
        this._scaleSimplePanel(gltfScene, sizeScale, 'x');
        break;
      case InsideType.DRAWER:
        this._scaleDrawerHeight(gltfScene, sizeScale);
        break;
      case InsideType.U_DRAWER:
        this._scaleUDrawerHeight(gltfScene, sizeScale);
        break;
      case 5:
        // Reserved for future implementation
        break;
      default:
        console.warn(`Unknown cabinet type: ${type}`);
    }
  }

  /**
   * Validates input parameters
   * @private
   */
  static _validateInputs(gltfScene, sizeScale) {
    if (!gltfScene || !gltfScene.userData) {
      console.error('Invalid gltfScene or missing userData');
      return false;
    }
    if (typeof sizeScale !== 'number' || sizeScale <= 0) {
      console.error('Invalid sizeScale: must be a positive number');
      return false;
    }
    return true;
  }

  /**
   * Scales simple panel components (HPANEL, VPANEL)
   * @private
   */
  static _scaleSimplePanel(gltfScene, sizeScale, axis) {
    const defaultSize = gltfScene.userData.sizeDefault[axis];
    const scaleRatio = sizeScale / defaultSize;
    gltfScene.scale[axis] = scaleRatio;
  }

  /**
   * Scales drawer components for depth
   * @private
   */
  static _scaleDrawerDepth(gltfScene, sizeScale, clonedModule) {
    const { sizeDefault } = gltfScene.userData;
    const sizeDifference = sizeScale - sizeDefault.x;
    // const sizeParent = GeometryUtils.getSizeRotation(clonedModule.group , gltf)
    const isExpanding = sizeDefault.x < sizeScale;

    gltfScene.traverse(child => {
      if (!child.isMesh) return;

      const { name } = child;
      const positionMultiplier = isExpanding ? 1 : -1;

      switch (name) {
        case this.COMPONENT_NAMES.MAT_TRUOC:
          child.position.x += sizeDifference;
          break;

        case this.COMPONENT_NAMES.MAT_PHAI:
        case this.COMPONENT_NAMES.MAT_TRAI:
        case this.COMPONENT_NAMES.TAM_DAY:
          this._scaleChildComponent(child, sizeScale, 'x');
          break;

        case this.COMPONENT_NAMES.RAY_PHAI:
        case this.COMPONENT_NAMES.RAY_TRAI:
          child.scale.x = (sizeScale - 0.01) / child.userData.sizeDefault.x;
          child.position.x = 0.01;
          break;
      }
    });
  }

  /**
   * Scales U-drawer components for depth
   * @private
   */
  static _scaleUDrawerDepth(gltfScene, sizeScale) {
    const { sizeDefault } = gltfScene.userData;
    const sizeDifference = sizeDefault.x - sizeScale;

    gltfScene.traverse(child => {
      if (!child.isMesh) return;

      const { name } = child;

      switch (name) {
        case this.COMPONENT_NAMES.NOC_U:
        case this.COMPONENT_NAMES.VACH_TRAI_U:
        case this.COMPONENT_NAMES.VACH_PHAI_U:
          this._scaleChildComponent(child, sizeScale, 'x', sizeDefault.x);
          break;

        case this.COMPONENT_NAMES.GG_TRAI:
        case this.COMPONENT_NAMES.GG_PHAI:
          child.position.x -= sizeDifference;
          break;
      }
    });
  }

  /**
   * Scales drawer components for width
   * @private
   */
  static _scaleDrawerWidth(gltfScene, sizeScale, sizeDefault) {
    gltfScene?.traverse(child => {
      if (child.isMesh) {
        let scaleZ;
        const temp = sizeScale / sizeDefault.z;
        if (sizeDefault.z < sizeScale) {
          if (child.name === 'MAT_TRUOC' || child.name === 'MAT_SAU') {
            scaleZ = (sizeScale - 0.061) / child.userData.sizeDefault.z;
            child.scale.z = scaleZ;
          }
          if (child.name === 'MAT_PHAI') {
            child.position.z = child.position.z - (sizeScale - sizeDefault.z);
          }
          if (child.name === 'TAM_DAY') {
            scaleZ = (sizeScale - 0.027) / child.userData.sizeDefault.z;
            child.scale.z = scaleZ;
          }
          if (child.name === 'RAY_PHAI') {
            child.position.z = child.position.z - (sizeScale - sizeDefault.z);
          }
        } else {
          if (child.name === 'MAT_TRUOC' || child.name === 'MAT_SAU') {
            scaleZ = (sizeScale - 0.061) / child.userData.sizeDefault.z;
            child.scale.z = scaleZ;
          }
          if (child.name === 'MAT_PHAI') {
            child.position.z = child.position.z + (sizeDefault.z - sizeScale);
          }
          if (child.name === 'TAM_DAY') {
            scaleZ = (sizeScale - 0.027) / child.userData.sizeDefault.z;
            child.scale.z = scaleZ;
          }
          if (child.name === 'RAY_PHAI') {
            child.position.z = child.position.z + (sizeDefault.z - sizeScale);
          }
        }
      }
    });
  }

  /**
   * Scales drawer components for width
   * @private
   */
  static _scaleDrawerHeight(gltfScene, sizeScale) {
    const { sizeDefault } = gltfScene.userData;
    const sizeDifference = sizeScale - sizeDefault.y;
    const isExpanding = sizeDefault.y < sizeScale;
    const positionMultiplier = isExpanding ? -1 : 1;

    gltfScene.traverse(child => {
      if (!child.isMesh) return;

      const { name } = child;

      switch (name) {
        case this.COMPONENT_NAMES.MAT_TRUOC:
        case this.COMPONENT_NAMES.MAT_SAU:
        case this.COMPONENT_NAMES.MAT_PHAI:
        case this.COMPONENT_NAMES.MAT_TRAI:
          this._scaleWithOffset(child, sizeScale, 'y', 0);
          break;
      }
    });
  }

  // Trong InsideCabinetHandler class

  /**
   * Scales U-drawer components for height
   * @private
   */
  static _scaleUDrawerHeight(gltfScene, sizeScale) {
    const { sizeDefault } = gltfScene.userData;
    const sizeDifference = sizeScale - sizeDefault.y;

    gltfScene.traverse(child => {
      if (!child.isMesh) return;

      const { name } = child;

      switch (name) {
        case this.COMPONENT_NAMES.VACH_TRAI_U:
        case this.COMPONENT_NAMES.VACH_PHAI_U:
          // Scale chiều cao của 2 vách bên
          this._scaleChildComponent(
            child,
            sizeScale - this.THICKNESS_BASE,
            'y',
            child.userData.sizeDefault.y
          );
          break;

        case this.COMPONENT_NAMES.NOC_U:
          // Di chuyển nóc lên/xuống
          child.position.y = sizeScale - this.THICKNESS_BASE;
          break;

        case this.COMPONENT_NAMES.GG_TRAI:
        case this.COMPONENT_NAMES.GG_PHAI:
          this._scaleChildComponent(
            child,
            sizeScale - this.THICKNESS_BASE,
            'y',
            child.userData.sizeDefault.y
          );
          break;
      }
    });
  }

  /**
   * Scales U-drawer components for width
   * @private
   */
  static _scaleUDrawerWidth(gltfScene, sizeScale, originalSize) {
    const { sizeDefault } = gltfScene.userData;
    const sizeDifference = sizeDefault.z - sizeScale;

    gltfScene.traverse(child => {
      if (!child.isMesh) return;

      const { name } = child;

      switch (name) {
        case this.COMPONENT_NAMES.NOC_U:
          this._scaleChildComponent(child, sizeScale, 'z', sizeDefault.z);
          break;

        case this.COMPONENT_NAMES.VACH_PHAI_U:
        case this.COMPONENT_NAMES.GG_PHAI:
          if (originalSize) {
            child.position.z = child.position.z - (sizeScale - originalSize.z);
          } else {
            child.position.z += sizeDifference;
          }
          break;
      }
    });
  }

  /**
   * Scales a child component along the specified axis
   * @private
   */
  static _scaleChildComponent(child, sizeScale, axis, defaultSize = null) {
    const childDefaultSize = defaultSize || child.userData.sizeDefault[axis];
    const scaleRatio = sizeScale / childDefaultSize;
    child.scale[axis] = scaleRatio;
  }

  /**
   * Scales a component with an offset applied
   * @private
   */
  static _scaleWithOffset(child, sizeScale, axis, offset) {
    const adjustedSize = sizeScale - offset;
    const childDefaultSize = child.userData.sizeDefault[axis];
    const scaleRatio = adjustedSize / childDefaultSize;
    child.scale[axis] = scaleRatio;
  }

  setInfoModelChild = gltfScene => {
    gltfScene?.traverse(child => {
      if (child.isMesh) {
        child.userData.type = gltfScene.userData.type;
        child.userData.parentGltf = gltfScene.uuid;
        const size = this.setSizeGLB(child);
        child.userData.sizeDefault = size;
      }
    });
    const size = this.setSizeGLB(gltfScene);
    gltfScene.userData.sizeDefault = size;
  };

  static settingUDrawerChild(
    clonedModule,
    meshSizeChild,
    gltfScene,
    modelChild,
    module,
    orientation,
    addModelToCabinet
  ) {
    return new Promise(resolve => {
      const numDrawer = gltfScene.userData?.infoChildDrawer?.numDrawer || 1;
      const heightDrawer =
        gltfScene.userData?.infoChildDrawer?.heightDrawer || 1;
      const bottomGap = gltfScene.userData?.infoChildDrawer?.bottomGap || 0;
      const topGap = gltfScene.userData?.infoChildDrawer?.topGap || 0;
      const sizeXDrawerChild =
        gltfScene.userData?.infoChildDrawer?.sizeXDrawerChild || 0;

      let leftMesh;
      let rightMesh;
      let topMesh;

      const clone = modelChild.clone();

      gltfScene?.traverse(child => {
        if (child.isMesh) {
          if (child.name === this.COMPONENT_NAMES.VACH_TRAI_U) {
            leftMesh = child;
          }
          if (child.name === this.COMPONENT_NAMES.VACH_PHAI_U) {
            rightMesh = child;
          }
          if (child.name === this.COMPONENT_NAMES.NOC_U) {
            topMesh = child;
          }
        }
      });
      if (!leftMesh || !rightMesh) {
        resolve();
        return;
      }

      const leftSizeZ = leftMesh.userData.sizeDefault.z;

      let sizeZ = 0;

      const leftWorldPos = new THREE.Vector3();
      const rightWorldPos = new THREE.Vector3();
      leftMesh.getWorldPosition(leftWorldPos);
      rightMesh.getWorldPosition(rightWorldPos);

      if (orientation === 'NORMAL') {
        sizeZ = leftWorldPos.z - rightWorldPos.z - leftSizeZ;
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        sizeZ = Math.abs(rightWorldPos.x - leftWorldPos.x) - leftSizeZ;
      }

      const leftPos = clonedModule.group.worldToLocal(
        leftMesh?.getWorldPosition(new THREE.Vector3()).clone()
      );
      const rightPos = clonedModule.group.worldToLocal(
        rightMesh?.getWorldPosition(new THREE.Vector3()).clone()
      );

      const sizeX = leftMesh.userData.sizeDefault.x - 0.045 - 0.017;

      modelChild.position.z = leftPos.z - leftSizeZ;
      modelChild.position.x = leftPos.x + 0.045;
      modelChild.position.y = leftPos.y + 0.02 + bottomGap;

      const sizeLeft = GeometryUtils.getSize(leftMesh);

      const sizeYDrawer =
        sizeLeft.y -
        0.02 -
        numDrawer * 0.05 -
        bottomGap * numDrawer -
        topGap * numDrawer;

      gltfScene.userData.infoChildDrawer.heightDrawer = sizeYDrawer;
      this.scaleHeight(modelChild, sizeYDrawer / numDrawer);

      this.scaleDepth(modelChild, sizeXDrawerChild);
      const sizeDefault = GeometryUtils.getSizeRotation(
        clonedModule.group,
        modelChild
      );
      this.scaleWidth(modelChild, sizeZ, sizeDefault);

      for (let i = 1; i < numDrawer; i++) {
        const clone1 = clone.clone();
        addModelToCabinet(clone1);
        const positionY =
          modelChild.position.y +
          i * (sizeYDrawer / numDrawer + 0.05 + bottomGap + topGap);
        clone1.position.y = positionY;
        clone1.position.z = leftPos.z - leftSizeZ;
        clone1.position.x = leftPos.x + 0.045;
        this.scaleHeight(clone1, sizeYDrawer / numDrawer);

        this.scaleDepth(clone1, sizeXDrawerChild, clonedModule);
        this.scaleWidth(clone1, sizeZ, sizeDefault);
      }
      resolve();
    });
  }

  static resetScaleUdrawerChild(
    clonedModule,
    gltfScene,
    orientation,
    scaleCabinetShelfWidth
  ) {
    return new Promise(resolve => {
      let leftMesh;
      let rightMesh;
      let topMesh;

      gltfScene?.traverse(child => {
        if (child.isMesh) {
          if (child.name === this.COMPONENT_NAMES.VACH_TRAI_U) {
            leftMesh = child;
          }
          if (child.name === this.COMPONENT_NAMES.VACH_PHAI_U) {
            rightMesh = child;
          }
          if (child.name === this.COMPONENT_NAMES.NOC_U) {
            topMesh = child;
          }
        }
      });
      if (!leftMesh || !rightMesh) {
        resolve();
        return;
      }

      const leftSizeZ = leftMesh.userData.sizeDefault.z;

      let sizeZ = 0;

      const leftWorldPos = new THREE.Vector3();
      const rightWorldPos = new THREE.Vector3();
      leftMesh.getWorldPosition(leftWorldPos);
      rightMesh.getWorldPosition(rightWorldPos);

      if (orientation === 'NORMAL') {
        sizeZ = leftWorldPos.z - rightWorldPos.z - leftSizeZ;
      } else if (
        orientation === 'ROTATED_90' ||
        orientation === 'ROTATED_270'
      ) {
        sizeZ = Math.abs(rightWorldPos.x - leftWorldPos.x) - leftSizeZ;
      }

      const leftPos = clonedModule.group.worldToLocal(
        leftMesh?.getWorldPosition(new THREE.Vector3()).clone()
      );

      for (let i = 1; i < clonedModule.listInside.length; i++) {
        const inside = clonedModule.listInside[i];
        if (
          inside.gltf.userData.type === InsideType.DRAWER &&
          inside.gltf.userData.isChildUDrawer &&
          inside.gltf.userData.parentUDrawerUuid === gltfScene.uuid
        ) {
          inside.gltf.position.z = leftPos.z - leftSizeZ;
          const sizeDefault = GeometryUtils.getSizeRotation(
            clonedModule.group,
            inside.gltf
          );
          // scaleCabinetShelfWidth(inside.gltf, originalSize, sizeZ);
          this.scaleWidth(inside.gltf, sizeZ, sizeDefault);
        }
      }
      resolve();
    });
  }
}

// import { InsideType } from "../utils/enum";

// export default class InsideCabinetHandler {
//   static scaleDepth(gltfScene, sizeScale) {
//     let sizeDefault = gltfScene.userData.sizeDefault;

//     switch (gltfScene.userData.type) {
//       case InsideType.HPANEL:
//         const scaleX0 = sizeScale / gltfScene.userData.sizeDefault.x;
//         gltfScene.scale.x = scaleX0;
//         break;
//       case InsideType.VPANEL:
//         const scaleX1 = sizeScale / gltfScene.userData.sizeDefault.x;
//         gltfScene.scale.x = scaleX1;
//         break;
//       case InsideType.DRAWER:
//         gltfScene?.traverse((child) => {
//           if (child.isMesh) {
//             let scaleX;
//             if (sizeDefault.x < sizeScale) {
//               if (child.name === "MAT_TRUOC") {
//                 child.position.x =
//                   child.position.x + (sizeScale - sizeDefault.x);
//               }
//               if (child.name === "MAT_PHAI" || child.name === "MAT_TRAI") {
//                 scaleX = sizeScale / child.userData.sizeDefault.x;
//                 child.scale.x = scaleX;
//               }
//               if (child.name === "TAM_DAY") {
//                 scaleX = sizeScale / child.userData.sizeDefault.x;
//                 child.scale.x = scaleX;
//               }
//               if (child.name === "RAY_PHAI" || child.name === "RAY_TRAI") {
//                 child.position.x =
//                   child.position.x + (sizeScale - sizeDefault.x);
//               }
//             } else {
//               if (child.name === "MAT_TRUOC") {
//                 child.position.x =
//                   child.position.x - (sizeDefault.x - sizeScale);
//               }
//               if (child.name === "MAT_PHAI" || child.name === "MAT_TRAI") {
//                 scaleX = sizeScale / child.userData.sizeDefault.x;
//                 child.scale.x = scaleX;
//               }
//               if (child.name === "TAM_DAY") {
//                 scaleX = sizeScale / child.userData.sizeDefault.x;
//                 child.scale.x = scaleX;
//               }
//               if (child.name === "RAY_PHAI" || child.name === "RAY_TRAI") {
//                 child.position.x =
//                   child.position.x - (sizeDefault.x - sizeScale);
//               }
//             }
//           }
//         });
//         break;
//       case InsideType.U_DRAWER:
//         gltfScene?.traverse((child) => {
//           if (child.isMesh) {
//             if (child.name === "NOC-U") {
//               const scaleX = sizeScale / sizeDefault.x;
//               child.scale.x = scaleX;
//             }
//             if (child.name === "VACH-TRAI-U") {
//               const scaleX = sizeScale / sizeDefault.x;
//               child.scale.x = scaleX;
//             }
//             if (child.name === "VACH-PHAI-U") {
//               const scaleX = sizeScale / sizeDefault.x;
//               child.scale.x = scaleX;
//             }
//             if (child.name === "GG-TRAI") {
//               child.position.x = child.position.x - (sizeDefault.x - sizeScale);
//             }
//             if (child.name === "GG-PHAI") {
//               child.position.x = child.position.x - (sizeDefault.x - sizeScale);
//             }
//           }
//         });

//         break;
//       case 5:
//         break;
//       default:
//         console.log("no type");
//     }
//   }

//   static scaleWidth(gltfScene, sizeScale) {
//     let sizeDefault = gltfScene.userData.sizeDefault;
//     switch (gltfScene.userData.type) {
//       case InsideType.HPANEL:
//         const scaleX0 = sizeScale / gltfScene.userData.sizeDefault.z;
//         gltfScene.scale.z = scaleX0;
//         break;
//       case InsideType.VPANEL:
//         const scaleX1 = sizeScale / gltfScene.userData.sizeDefault.x;
//         gltfScene.scale.x = scaleX1;
//         break;
//       case InsideType.DRAWER:
//         gltfScene?.traverse((child) => {
//           if (child.isMesh) {
//             let scaleZ;
//             const temp = sizeScale / sizeDefault.z;
//             if (sizeDefault.z < sizeScale) {
//               if (child.name === "MAT_TRUOC" || child.name === "MAT_SAU") {
//                 scaleZ = (sizeScale - 0.061) / child.userData.sizeDefault.z;
//                 child.scale.z = scaleZ;
//               }
//               if (child.name === "MAT_PHAI") {
//                 child.position.z =
//                   child.position.z - (sizeScale - sizeDefault.z);
//               }
//               if (child.name === "TAM_DAY") {
//                 scaleZ = (sizeScale - 0.027) / child.userData.sizeDefault.z;
//                 child.scale.z = scaleZ;
//               }
//               if (child.name === "RAY_PHAI") {
//                 child.position.z =
//                   child.position.z - (sizeScale - sizeDefault.z);
//               }
//             } else {
//               if (child.name === "MAT_TRUOC" || child.name === "MAT_SAU") {
//                 scaleZ = (sizeScale - 0.061) / child.userData.sizeDefault.z;
//                 child.scale.z = scaleZ;
//               }
//               if (child.name === "MAT_PHAI") {
//                 child.position.z =
//                   child.position.z + (sizeDefault.z - sizeScale);
//               }
//               if (child.name === "TAM_DAY") {
//                 scaleZ = (sizeScale - 0.027) / child.userData.sizeDefault.z;
//                 child.scale.z = scaleZ;
//               }
//               if (child.name === "RAY_PHAI") {
//                 child.position.z =
//                   child.position.z + (sizeDefault.z - sizeScale);
//               }
//             }
//           }
//         });
//         break;
//       case InsideType.U_DRAWER:
//         gltfScene?.traverse((child) => {
//           if (child.isMesh) {
//             if (child.name === "NOC-U") {
//               const scaleZ = sizeScale / sizeDefault.z;
//               child.scale.z = scaleZ;
//             }
//             if (child.name === "VACH-PHAI-U") {
//               child.position.z = child.position.z + (sizeDefault.z - sizeScale);
//             }
//             if (child.name === "GG-PHAI") {
//               child.position.z = child.position.z + (sizeDefault.z - sizeScale);
//             }
//           }
//         });

//         break;
//       case 5:
//         break;
//       default:
//         console.log("no type");
//     }
//   }
// }
